import React, { useContext, useState } from 'react';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { SecondaryButton } from '@hz-design-system/web-ui';

import getBids from '../../repositories/getBids';

import { trackGAEvent } from '../../../../utils/gaTracking';
import EnvironmentContext from '../../../../contexts/EnvironmentContext';
import LoginDialogContainer from '../../../../components/LoginDialog/LoginDialogContainer';

import { TListingClient } from 'types/TListing';
import { TBiddingClient } from 'types/TBidding';

import Classes from './OneStepBidding.scss';

let OneStepBiddingMessageDialog;
let ConfirmDeleteBidModal;

type TOneStepBiddingProps = {
  itemId: TListingClient['itemId'];
  priceType: TListingClient['priceInfo']['priceType'];
  bids: TListingClient['bidsInfo']['bids'];
  title: TListingClient['title'];
  currentMinimumBid?: TListingClient['bidsInfo']['currentMinimumBid'];
  sellerType: TListingClient['seller']['sellerType'];
  withPhoneNumber?: boolean;
};

const OneStepBidding = ({
  priceType,
  itemId,
  bids,
  title,
  currentMinimumBid,
  sellerType,
  withPhoneNumber,
}: TOneStepBiddingProps) => {
  const { t } = useI18nContext();
  const { userLoggedIn, xsrfToken, userDetails: { isAdOwner = false } = {} } = useContext(EnvironmentContext);
  const [bidToDelete, setBidToDelete] = useState(undefined);
  const [adBids, setAdBids] = useState<TListingClient['bidsInfo']['bids']>(bids);

  const [biddingMessageDialogLoaded, setBiddingMessageDialogLoaded] = useState(false);
  const [confirmDeleteBidModalLoaded, setConfirmDeleteBidModalLoaded] = useState(false);
  const [showBiddingMessageDialog, setShowBiddingMessageDialog] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showConfirmDeleteBidModal, setShowConfirmDeleteBidModal] = useState(false);

  const fetchBids = async () => {
    const response: TBiddingClient = await getBids({ itemId, xsrfToken });
    if (response.bids) {
      setAdBids(response.bids);
    }
  };

  const loadBiddingMessageDialog = async () => {
    if (!biddingMessageDialogLoaded) {
      try {
        OneStepBiddingMessageDialog = (
          await import(/* webpackChunkName: "BiddingMessageDialog" */ './OneStepBiddingMessageDialog')
        ).default;
        setBiddingMessageDialogLoaded(true);
      } catch (err) {}
    }
  };

  const loadConfirmDeleteBidModal = async () => {
    if (!confirmDeleteBidModalLoaded) {
      try {
        ConfirmDeleteBidModal = (
          await import(/* webpackChunkName: "ConfirmDeleteBidModal" */ './ConfirmDeleteBidModal')
        ).default;
        setConfirmDeleteBidModalLoaded(true);
      } catch (err) {}
    }
  };

  const handleBiddingClick = async () => {
    if (userLoggedIn) {
      trackGAEvent('R2SBidBegin');
      await loadBiddingMessageDialog();
      setShowBiddingMessageDialog(true);
    } else {
      setShowLoginDialog(true);
    }
  };

  const handleLoginSuccess = async () => {
    trackGAEvent('R2SBidBegin');
    await loadBiddingMessageDialog();
    setShowBiddingMessageDialog(true);
  };

  const isReserved = priceType === 'RESERVED';

  const handleShowConfirmDeleteBidModal = async (bid) => {
    await loadConfirmDeleteBidModal();
    setBidToDelete(bid);
    setShowBiddingMessageDialog(false);
    setShowConfirmDeleteBidModal(true);
  };

  const handleBackConfirmDeleteBidModal = () => {
    setBidToDelete(undefined);
    setShowBiddingMessageDialog(true);
    setShowConfirmDeleteBidModal(false);
  };

  const handleCancelConfirmDeleteBidModal = () => {
    setBidToDelete(undefined);
    setShowConfirmDeleteBidModal(false);
  };

  const handleSuccessBidsUpdate = async () => {
    setBidToDelete(undefined);
    await fetchBids();
  };

  const handleSuccessBidsDelete = async () => {
    await fetchBids();
    handleBackConfirmDeleteBidModal();
  };

  if (isReserved || isAdOwner) {
    return null;
  }

  return (
    <div className={Classes.root}>
      <SecondaryButton className={Classes.bidButton} onClick={handleBiddingClick}>
        {/*// TODO request translation*/}
        {t('bidding.addBid.button.label')}
      </SecondaryButton>

      {biddingMessageDialogLoaded && showBiddingMessageDialog && (
        <OneStepBiddingMessageDialog
          bids={adBids}
          title={title}
          currentMinimumBid={currentMinimumBid}
          priceType={priceType}
          itemId={itemId}
          sellerType={sellerType}
          onClose={() => setShowBiddingMessageDialog(false)}
          withPhoneNumber={withPhoneNumber}
          onDeleteBidHandler={handleShowConfirmDeleteBidModal}
          onSuccessBidsUpdate={handleSuccessBidsUpdate}
        />
      )}

      {confirmDeleteBidModalLoaded && showConfirmDeleteBidModal && (
        <ConfirmDeleteBidModal
          onConfirm={handleSuccessBidsDelete}
          onBack={handleBackConfirmDeleteBidModal}
          onCancel={handleCancelConfirmDeleteBidModal}
          bid={bidToDelete}
        />
      )}

      {showLoginDialog && <LoginDialogContainer onClose={setShowLoginDialog} onSuccess={handleLoginSuccess} />}
    </div>
  );
};

export default OneStepBidding;
